import { getCurationContentCentres } from '../../../helpers/curation-storefront'
import { SelectListMenuModal } from '../../../components/modal/app_modals/select-list-menu-modal'
import { ListMenuItem } from '../../../redux-toolkits/list-menu/listMenu.types'
import { useEffect, useState } from 'react'
import ModalComponent from '../../../components/modal'
import { AddExploreModal } from '../../../components/modal/app_modals/addExploreModal'
import { AddHomepageBannerModal } from '../../../components/modal/app_modals/addHomeBannerModal'
import { SelectCategoriesModal } from '../../../components/modal/app_modals/selectCategoriesModal'
import { SelectCreatorModal } from '../../../components/modal/app_modals/selectCreatorModal'
import { SelectEssentialsModal } from '../../../components/modal/app_modals/selectEssentialModal'
import { SelectGoodListModal } from '../../../components/modal/app_modals/selectGoodListModal'
import { SelectProductModal } from '../../../components/modal/app_modals/selectProductModal'
import { SelectSubCategoriesModal } from '../../../components/modal/app_modals/selectSubCategoriesModal'
import { Button } from '../../../components/button'
import { Checkbox } from '../../../components/inputs/Checkbox'
import { SearchInput } from '../../../components/inputs/searchInput'
import { useGetAllEventsQuery } from '../../../redux-toolkits/events/events.slice'
import { SingleEvent } from '../../../redux-toolkits/events/events.type'
import {
  HomepageBannerContent,
  ManualContent,
} from '../../../redux-toolkits/home-page/homePage.type'
import { CurationContentType } from '../../../redux-toolkits/home-page-v2.1/homePageV21.type'
import { SingleEssential } from '../../../redux-toolkits/lists/list.types'
import { SingleSubCategory } from '../../../redux-toolkits/products/products.type'
import { FulfilmentCentre } from '../../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { type QueryProps } from '../../../redux-toolkits/shared/shared.type'
import { Product } from '../../../redux-toolkits/products/products.type'
import { StrippedVendor } from '../../../redux-toolkits/vendors/vendors.types'
import { Article } from '../../../redux-toolkits/lists/list.types'
import { ListCategoryDoc } from '../../../redux-toolkits/categories/categories.type'

export interface EditorContentItem {
  subcategories?: SingleSubCategory[]
  key: string
  type: CurationContentType
  value?: Record<string, any>
  banner?: HomepageBannerContent
  manualContent?: Omit<ManualContent, '_id'>
}

const EventsPickerModal = ({
  open,
  setOpen,
  selected,
  onSave,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  selected: SingleEvent[]
  onSave: (events: SingleEvent[]) => void
}) => {
  const [events, setEvents] = useState<SingleEvent[]>(selected)
  // The events endpoint requires a date range. Curation picks from the events
  // happening in the current month.
  const [query, setQuery] = useState<QueryProps>({
    skip: 0,
    limit: 30,
    isCustomDateRange: false,
    filter: 'this_year',
  })
  const { data, isFetching } = useGetAllEventsQuery(
    { ...query, isActive: true },
    {
      skip: !open,
      refetchOnMountOrArgChange: true,
    },
  )

  useEffect(() => {
    if (open) setEvents(selected)
  }, [open, selected])

  return (
    <ModalComponent
      childrenClassName="md:!max-w-[520px]"
      open={open}
      setOpen={setOpen}
      title="Select events"
    >
      <div className="flex max-h-[620px] flex-col gap-4">
        <SearchInput
          className="!max-w-full"
          placeholder="Search events"
          searchFunction={(search) =>
            setQuery({ ...query, search: search || undefined, skip: 0 })
          }
        />
        <div className="flex-1 space-y-2 overflow-y-auto">
          {isFetching ? (
            <p className="py-8 text-center text-sm text-sec-black">
              Loading events…
            </p>
          ) : null}
          {(data?.data?.data || []).map((event) => {
            const checked = events.some((item) => item._id === event._id)
            return (
              <Checkbox
                checkStatus={checked}
                className="w-full border-b py-3 last:border-b-0"
                handleChange={(isChecked) =>
                  setEvents((current) =>
                    isChecked
                      ? [...current, event]
                      : current.filter((item) => item._id !== event._id),
                  )
                }
                key={event._id}
                text={
                  <div className="flex items-center gap-3">
                    <img
                      alt=""
                      className="h-14 w-14 rounded-lg bg-gray-100 object-cover"
                      src={event.images?.[0]}
                    />
                    <div>
                      <p className="font-medium">{event.name}</p>
                      <p className="text-xs text-sec-black">
                        {event.eventTicketSettings?.venue}
                      </p>
                    </div>
                  </div>
                }
              />
            )
          })}
        </div>
        <Button
          label="Use selected events"
          onClick={() => onSave(events)}
          primary
          type="button"
        />
      </div>
    </ModalComponent>
  )
}

const valuesFor = <T,>(
  items: EditorContentItem[],
  type: CurationContentType,
): T[] =>
  items
    .filter((item) => item.type === type && item.value)
    .map((item) => item.value as T)

export const ContentPickerModals = ({
  pickerType,
  setPickerType,
  items,
  replaceType,
  bannerEditKey,
  saveBanner,
  saveManualItem,
  productSection,
  maxCategorySelection = 12,
  maxSubcategorySelection = 12,
  fulfilmentCentre,
}: {
  pickerType: CurationContentType | null
  setPickerType: (type: CurationContentType | null) => void
  items: EditorContentItem[]
  replaceType: (
    type: CurationContentType,
    values: Array<Record<string, any>>,
  ) => void
  bannerEditKey?: string
  saveBanner: (banner: HomepageBannerContent, key?: string) => void
  saveManualItem: (content: Omit<ManualContent, '_id'>, key?: string) => void
  productSection?: 'LIFESTYLE' | 'FOOD' | 'EVENT'
  maxCategorySelection?: number
  maxSubcategorySelection?: number
  fulfilmentCentre?: FulfilmentCentre
}) => {
  const close = () => setPickerType(null)
  const contentCentres = getCurationContentCentres(
    fulfilmentCentre ? [fulfilmentCentre] : undefined,
  )
  const contentCentreId = contentCentres?.[0]?._id
  const editItem = bannerEditKey
    ? items.find((item) => item.key === bannerEditKey)
    : undefined

  return (
    <>
      <SelectProductModal
        maxSelection={12}
        onCloseCallback={close}
        onSuccessCallback={(products) => {
          replaceType('PRODUCTS', products)
          close()
        }}
        open={pickerType === 'PRODUCTS'}
        query={{
          section: productSection,
          status: 'active',
          fulfillmentCenterId: contentCentreId,
        }}
        selectedProducts={valuesFor<Product>(items, 'PRODUCTS')}
        setOpen={(open) => !open && close()}
      />

      <SelectCreatorModal
        maxSelection={12}
        onCloseCallback={close}
        onSuccessCallback={(creators) => {
          replaceType('CREATORS', creators)
          close()
        }}
        open={pickerType === 'CREATORS'}
        fulfillmentCenters={contentCentres}
        section={productSection}
        status="active"
        selectedCreators={valuesFor<StrippedVendor>(items, 'CREATORS')}
        setOpen={(open) => !open && close()}
      />

      {pickerType === 'LIST_MENUS' ? (
        <SelectListMenuModal
          fulfilmentCentre={fulfilmentCentre}
          selectedMenus={valuesFor<ListMenuItem>(items, 'LIST_MENUS')}
          onClose={close}
          onSave={(menus) => {
            replaceType('LIST_MENUS', menus)
            close()
          }}
        />
      ) : null}

      <SelectGoodListModal
        fulfilmentCentreId={contentCentreId}
        maxSelection={12}
        onCloseCallback={close}
        onSuccessCallback={(lists) => {
          replaceType('GOOD_LISTS', lists)
          close()
        }}
        open={pickerType === 'GOOD_LISTS'}
        section={productSection}
        selectedGoodLists={valuesFor<Article>(items, 'GOOD_LISTS')}
        setOpen={(open) => !open && close()}
      />

      <SelectEssentialsModal
        maxSelection={12}
        onCloseCallback={close}
        onSuccessCallback={(lists) => {
          replaceType('ESSENTIALS', lists)
          close()
        }}
        open={pickerType === 'ESSENTIALS'}
        section={productSection}
        selectedEssentials={valuesFor<SingleEssential>(items, 'ESSENTIALS')}
        setOpen={(open) => !open && close()}
      />

      <SelectCategoriesModal
        maxSelection={maxCategorySelection}
        onCloseCallback={close}
        onSuccessCallback={(categories) => {
          replaceType('CATEGORIES', categories)
          close()
        }}
        open={pickerType === 'CATEGORIES'}
        activeOnly
        section={productSection}
        selectedCategories={valuesFor<ListCategoryDoc>(items, 'CATEGORIES')}
        setOpen={(open) => !open && close()}
      />

      <SelectSubCategoriesModal
        maxSelection={maxSubcategorySelection}
        onCloseCallback={close}
        onSuccessCallback={(subcategories) => {
          replaceType('SUBCATEGORIES', subcategories)
          close()
        }}
        open={pickerType === 'SUBCATEGORIES'}
        activeOnly
        section={productSection}
        selectedSubCategories={valuesFor<SingleSubCategory>(
          items,
          'SUBCATEGORIES',
        )}
        setOpen={(open) => !open && close()}
      />

      <EventsPickerModal
        onSave={(events) => {
          replaceType('EVENTS', events)
          close()
        }}
        open={pickerType === 'EVENTS'}
        selected={valuesFor<SingleEvent>(items, 'EVENTS')}
        setOpen={(open) => !open && close()}
      />

      <AddHomepageBannerModal
        content={editItem?.banner}
        mode={bannerEditKey ? 'edit' : 'create'}
        onCloseCallback={close}
        onSuccessCallback={(banner) => {
          saveBanner(banner as HomepageBannerContent, bannerEditKey)
          close()
        }}
        open={pickerType === 'HOMEPAGE_BANNER'}
        setOpen={(open) => !open && close()}
      />

      <AddExploreModal
        content={
          editItem?.manualContent
            ? ({ ...editItem.manualContent, _id: '' } as ManualContent)
            : undefined
        }
        mode={bannerEditKey ? 'edit' : 'create'}
        onCloseCallback={close}
        onSuccessCallback={(content) => {
          saveManualItem(content, bannerEditKey)
          close()
        }}
        open={pickerType === 'MANUAL_ITEM'}
        setOpen={(open) => !open && close()}
      />
    </>
  )
}
